import moment from 'moment';

import {
  ICountingChannel,
  ICountingChannelWeeklySeries,
  ICountingKpi,
  ICountingSite,
  ICountingSourceDefinition,
  ICountingSourceState,
  ICountingWeeklyPoint,
  ICountingWeeklySeries,
  TCountingFreshness,
} from '../types';

import { toulouseCounters } from './toulouse';

const pageSize = 100;
const maxPages = 50;
const maxDays = 7;
const kpiMaxDays = 31;
export const countingWeeklyWindowWeeks = 12;
const nantesCountersUrl =
  'https://data.nantesmetropole.fr/api/explore/v2.1/catalog/datasets/244400404_comptages-velo-nantes-metropole-boucles-comptage/records';
const toursMetropoleCountersUrl =
  'https://data.tours-metropole.fr/api/explore/v2.1/catalog/datasets/comptage-velo-compteurs-syndicat-des-mobilites-de-touraine/records';
const grandLyonSitesUrl =
  'https://data.grandlyon.com/fr/datapusher/ws/rdata/pvo_patrimoine_voirie.pvocomptagesite/all.json';
const grandLyonChannelsUrl =
  'https://data.grandlyon.com/fr/datapusher/ws/rdata/pvo_patrimoine_voirie.pvocomptagechannel/all.json';
const grandLyonMeasuresUrl =
  'https://data.grandlyon.com/fr/datapusher/ws/timeseries/pvo_patrimoine_voirie.pvocomptagemeasure/all.json';
const grandLyonMeasureChannelChunkSize = 100;
const montpellierCountersUrl = 'https://portail-api-data.montpellier3m.fr/ecocounter';
const montpellierTimeseriesBaseUrl =
  'https://portail-api-data.montpellier3m.fr/ecocounter_timeseries';
const countingDayTotalsConcurrency = 6;
let grandLyonSitesPromise: Promise<IGrandLyonSiteRecord[]> | null = null;
let grandLyonBicycleChannelsPromise: Promise<IGrandLyonChannelRecord[]> | null = null;
const grandLyonRangeAggregationCache = new Map<string, Promise<IGrandLyonRangeAggregation>>();
let montpellierBicycleCountersPromise: Promise<IMontpellierCounterRecord[]> | null = null;
const montpellierDayTotalsCache = new Map<string, Promise<number>>();
const countingRangeTotalsCache = new Map<string, Promise<IDailyTotalPoint[]>>();
const countingDayTotalsCache = new Map<string, Promise<number | null>>();
const montpellierRangeTotalsCache = new Map<string, Promise<IDailyTotalPoint[]>>();

interface IOpendatasoftResponse<T> {
  total_count?: number;
  results: T[];
}

interface IParisRecord {
  id?: string;
  id_compteur?: string;
  name?: string;
  nom_compteur?: string;
  sum_counts?: number;
  date?: string;
  coordinates?: { lon?: number; lat?: number };
}

interface INantesRecord {
  boucle_num?: number;
  boucle_libelle?: string;
  jour?: string;
  total?: number;
  probabilite_presence_anomalie?: number | null;
}

interface INantesCounterRecord {
  boucle_num?: number;
  geolocalisation?: { lon?: number; lat?: number };
}

interface IEcoCounterRecord {
  id?: string;
  date?: string;
  counts?: number | null;
  name?: string | null;
  sens?: string | null;
  counter?: string | null;
  geo?: { lon?: number; lat?: number } | null;
}

interface IToursMetropoleRecord {
  id?: string;
  date?: string;
  counts?: number | null;
  nom_compteur?: string | null;
  counter?: string | null;
  coordinates?: { lon?: number; lat?: number } | null;
}

interface IToursMetropoleCounterRecord {
  id_compteur?: string;
  coordinates?: { lon?: number; lat?: number } | null;
}

interface IToulouseRecord {
  id?: string;
  date?: string;
  counts?: number | null;
  travel_mode?: string;
  direction?: string;
  direction_name?: string;
}

interface IAngletRecord {
  id?: string;
  date?: string;
  counts?: number | null;
  travel_mode?: string;
  direction_name?: string;
  name?: string;
  location?: { lon?: number; lat?: number };
}

interface ISaintNazaireRecord {
  id?: string;
  date?: string;
  counts?: number | null;
  name?: string;
  location?: { lon?: number; lat?: number };
  travel_modes?: string;
  first_data?: string;
}

interface IGrandLyonResponse<T> {
  nb_results?: number;
  next?: string | null;
  values: T[];
}

interface IGrandLyonSiteRecord {
  site_id?: string;
  site_name?: string;
  lon?: number;
  lat?: number;
  xlong?: number;
  ylat?: number;
}

interface IGrandLyonChannelRecord {
  channel_id?: string;
  site_id?: string;
  mobility_type?: string;
  direction?: string;
  provider_direction_name?: string;
}

interface IGrandLyonMeasureRecord {
  channel_id?: string;
  start_datetime?: string;
  count?: number | null;
}

interface IMontpellierValue<T> {
  value?: T;
  metadata?: { TimeInstant?: { value?: string } };
}

interface IMontpellierCounterRecord {
  id?: string;
  intensity?: IMontpellierValue<number>;
  location?: IMontpellierValue<{ coordinates?: number[] }>;
  vehicleType?: IMontpellierValue<string>;
}

interface IMontpellierTimeseriesResponse {
  index?: string[];
  values?: number[];
}

export interface ISourceResult {
  source: ICountingSourceState;
  sites: ICountingSite[];
}

interface IDailyTotalRecord {
  total?: number;
}

interface IDailyTotalPoint {
  day: string;
  count: number;
}

interface IGrandLyonSiteChannelLabel {
  channelId: string;
  direction?: string;
}

interface IGrandLyonRangeAggregation {
  globalDailyTotals: IDailyTotalPoint[];
  siteChannelDailyTotals: Record<string, Record<string, IDailyTotalPoint[]>>;
  siteChannelLabels: Record<string, IGrandLyonSiteChannelLabel[]>;
}

export async function getCountingData(sources: ICountingSourceDefinition[]): Promise<{
  sources: ICountingSourceState[];
  sites: ICountingSite[];
  kpi: ICountingKpi;
}> {
  const results = await Promise.all(sources.map((source) => getCountingSourceData(source)));
  const sites = results.flatMap(({ sites }) => sites);

  return {
    sources: results.map(({ source }) => source),
    sites,
    kpi: await getCountingKpi(sources, results),
  };
}

export async function getCountingKpi(
  sources: ICountingSourceDefinition[],
  results: ISourceResult[],
): Promise<ICountingKpi> {
  try {
    return await getKpi(sources, results);
  } catch (err) {
    console.error('[Counts][getKpi]', err);

    return { count: 0, countedAt: null, sourceNames: [], territories: [] };
  }
}

export async function getCountingWeeklySeries(
  sources: ICountingSourceDefinition[],
): Promise<ICountingWeeklySeries> {
  const enabledSources = sources.filter(({ enabled }) => enabled);
  const endDay = moment().subtract(1, 'day');
  const startWeek = endDay
    .clone()
    .startOf('isoWeek')
    .subtract(countingWeeklyWindowWeeks - 1, 'weeks');
  const startDay = startWeek.format('YYYY-MM-DD');
  const endExclusive = endDay.clone().add(1, 'day').format('YYYY-MM-DD');
  const weekStarts = Array.from({ length: countingWeeklyWindowWeeks }, (_, index) =>
    startWeek.clone().add(index, 'week').format('YYYY-MM-DD'),
  );
  const pointsMap = weekStarts.reduce<Record<string, ICountingWeeklyPoint>>((res, weekStart) => {
    res[weekStart] = {
      weekStart,
      count: 0,
      cityCounts: {},
      sourceNames: [],
    };

    return res;
  }, {});
  const results = await Promise.all(
    enabledSources.map(async (source) => {
      try {
        return {
          cityName: source.cityName,
          totals: await getSourceDailyTotalsForRange(source, startDay, endExclusive),
        };
      } catch (err) {
        console.error('[Counts][getCountingWeeklySeries]', source.id, err);

        return {
          cityName: source.cityName,
          totals: null,
        };
      }
    }),
  );

  const failedSources = results
    .filter(({ totals }) => totals === null)
    .map(({ cityName }) => cityName);

  results.forEach(({ cityName, totals }) => {
    if (!totals) return;

    const weeklyTotals = aggregateDailyTotalsByWeek(totals);

    Object.entries(weeklyTotals).forEach(([weekStart, count]) => {
      const point = pointsMap[weekStart];
      if (!point) return;

      point.count += count;
      point.cityCounts[cityName] = count;
      point.sourceNames.push(cityName);
    });
  });

  return {
    points: weekStarts.map((weekStart) => pointsMap[weekStart]),
    failedSources,
  };
}

export async function getCountingSiteWeeklySeries(
  source: ICountingSourceDefinition,
  site: ICountingSite,
): Promise<ICountingChannelWeeklySeries[]> {
  const endDay = moment().subtract(1, 'day');
  const startWeek = endDay
    .clone()
    .startOf('isoWeek')
    .subtract(countingWeeklyWindowWeeks - 1, 'weeks');
  const startDay = startWeek.format('YYYY-MM-DD');
  const endExclusive = endDay.clone().add(1, 'day').format('YYYY-MM-DD');
  const weekStarts = Array.from({ length: countingWeeklyWindowWeeks }, (_, index) =>
    startWeek.clone().add(index, 'week').format('YYYY-MM-DD'),
  );

  if (source.type === 'grandLyon') {
    return getGrandLyonSiteWeeklySeries(site, startDay, endExclusive, weekStarts);
  }

  if (source.type === 'montpellier') {
    return getMontpellierSiteWeeklySeries(site, startDay, endExclusive, weekStarts);
  }

  const records = await getRangeRecords(source, startDay, endExclusive);

  return buildChannelWeeklySeries(
    site.channels,
    aggregateSourceSiteChannelTotalsByDay(source, site, records),
    weekStarts,
  );
}

export async function getCountingSourceData(
  source: ICountingSourceDefinition,
): Promise<ISourceResult> {
  if (!source.enabled) {
    return {
      source: {
        id: source.id,
        name: source.name,
        cityName: source.cityName,
        enabled: false,
        loading: false,
        sitesCount: 0,
        channelsCount: 0,
      },
      sites: [],
    };
  }

  try {
    if (source.type === 'grandLyon') return getGrandLyonSourceData(source);
    if (source.type === 'montpellier') return getMontpellierSourceData(source);

    const latestDay = await getLatestDay(source);
    const records = latestDay ? await getDayRecords(source, latestDay) : [];
    const nantesCoordinates =
      source.type === 'nantes' ? await getNantesCountersCoordinates() : undefined;
    const toursMetropoleCoordinates =
      source.type === 'toursMetropole' ? await getToursMetropoleCountersCoordinates() : undefined;
    const sites = parseRecords(source, records, nantesCoordinates, toursMetropoleCoordinates);

    return {
      source: {
        id: source.id,
        name: source.name,
        cityName: source.cityName,
        enabled: true,
        loading: false,
        latestCountedAt: latestDay,
        sitesCount: sites.length,
        channelsCount: sites.reduce((count, site) => count + site.channels.length, 0),
      },
      sites,
    };
  } catch (err) {
    return {
      source: {
        id: source.id,
        name: source.name,
        cityName: source.cityName,
        enabled: true,
        loading: false,
        error: err instanceof Error ? err.message : 'Unknown error',
        sitesCount: 0,
        channelsCount: 0,
      },
      sites: [],
    };
  }
}

function getEmptyEnabledSourceResult(source: ICountingSourceDefinition): ISourceResult {
  return {
    source: {
      id: source.id,
      name: source.name,
      cityName: source.cityName,
      enabled: true,
      loading: false,
      sitesCount: 0,
      channelsCount: 0,
    },
    sites: [],
  };
}

async function getLatestDay(source: ICountingSourceDefinition): Promise<string | null> {
  const url = new URL(source.recordsUrl);
  url.searchParams.set('limit', '1');
  url.searchParams.set('order_by', `${source.dateField} desc`);
  if (
    source.type === 'ecoCounter' ||
    source.type === 'toursMetropole' ||
    source.type === 'toulouse' ||
    source.type === 'saintNazaire'
  )
    url.searchParams.set('where', getNonNullCountsWhere(source));

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

  const data = (await response.json()) as IOpendatasoftResponse<
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord
  >;
  const record = data.results[0];

  return record ? toDayKey(getRecordDate(source, record)) : null;
}

async function getDayRecords(
  source: ICountingSourceDefinition,
  day: string,
): Promise<
  Array<
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord
  >
> {
  const records: Array<
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord
  > = [];

  for (let page = 0; page < maxPages; page += 1) {
    const url = new URL(source.recordsUrl);
    url.searchParams.set('limit', String(pageSize));
    url.searchParams.set('offset', String(page * pageSize));
    url.searchParams.set('order_by', `${source.dateField} desc`);
    url.searchParams.set('where', getDayWhere(source, day));

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

    const data = (await response.json()) as IOpendatasoftResponse<
      | IParisRecord
      | INantesRecord
      | IEcoCounterRecord
      | IToursMetropoleRecord
      | IToulouseRecord
      | IAngletRecord
      | ISaintNazaireRecord
    >;
    const pageRecords = data.results || [];

    records.push(...pageRecords);

    if (
      pageRecords.length < pageSize ||
      (data.total_count !== undefined && records.length >= data.total_count)
    )
      break;
  }

  return records;
}

async function getRangeRecords(
  source: ICountingSourceDefinition,
  startDay: string,
  endExclusive: string,
): Promise<
  Array<
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord
  >
> {
  const records: Array<
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord
  > = [];

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const url = new URL(source.recordsUrl);
    url.searchParams.set('limit', String(pageSize));
    url.searchParams.set('offset', String(pageIndex * pageSize));
    url.searchParams.set('where', getRangeWhere(source, startDay, endExclusive));

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

    const data = (await response.json()) as IOpendatasoftResponse<
      | IParisRecord
      | INantesRecord
      | IEcoCounterRecord
      | IToursMetropoleRecord
      | IToulouseRecord
      | IAngletRecord
      | ISaintNazaireRecord
    >;
    const pageRecords = data.results || [];

    records.push(...pageRecords);

    if (
      pageRecords.length < pageSize ||
      (data.total_count !== undefined && records.length >= data.total_count)
    )
      break;

    if (pageIndex === maxPages - 1) {
      const totalCount = data.total_count;
      const truncated = totalCount === undefined || records.length < totalCount;

      if (truncated) {
        throw new Error(
          `Range records truncated for ${source.id}: fetched ${records.length} rows with limit ${pageSize * maxPages}`,
        );
      }
    }
  }

  return records;
}

async function getDayTotal(source: ICountingSourceDefinition, day: string): Promise<number | null> {
  const cacheKey = `${source.id}:${day}`;
  const cached = countingDayTotalsCache.get(cacheKey);
  if (cached) return cached;

  const request = (async () => {
    if (source.type === 'grandLyon') {
      const sites = await getGrandLyonSitesForDay(source, day);
      if (sites.length <= 0) return null;

      return sites.reduce((count, site) => count + site.count, 0);
    }

    if (source.type === 'montpellier') {
      const counters = await getMontpellierBicycleCounters();
      const activeCounters = getMontpellierCountersForDay(counters, day);
      const totals = await Promise.all(
        activeCounters.map(({ id }) => (id ? getMontpellierDayTotal(id, day) : 0)),
      );
      if (totals.length <= 0) return null;

      return totals.reduce((count, total) => count + total, 0);
    }

    const url = new URL(source.recordsUrl);
    url.searchParams.set(
      'select',
      source.type === 'paris'
        ? 'sum(sum_counts) as total'
        : source.type === 'ecoCounter' ||
            source.type === 'toursMetropole' ||
            source.type === 'toulouse' ||
            source.type === 'anglet' ||
            source.type === 'saintNazaire'
          ? 'sum(counts) as total'
          : 'sum(total) as total',
    );
    url.searchParams.set('where', getDayWhere(source, day));
    url.searchParams.set('limit', '1');

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

    const data = (await response.json()) as IOpendatasoftResponse<IDailyTotalRecord>;
    if (!data.total_count || data.total_count <= 0) return null;

    return data.results[0]?.total || 0;
  })();

  countingDayTotalsCache.set(cacheKey, request);

  return request;
}

async function getSourceDailyTotalsForRange(
  source: ICountingSourceDefinition,
  startDay: string,
  endExclusive: string,
): Promise<IDailyTotalPoint[]> {
  const cacheKey = `${source.id}:${startDay}:${endExclusive}`;
  const cached = countingRangeTotalsCache.get(cacheKey);
  if (cached) return cached;

  const request = (async () => {
    if (source.type === 'grandLyon') {
      return getGrandLyonDailyTotalsForRange(startDay, endExclusive);
    }

    if (source.type === 'montpellier') {
      return getMontpellierDailyTotalsForRange(startDay, endExclusive);
    }

    return getDailyTotalsForRangeFromDayQueries(source, startDay, endExclusive);
  })();

  countingRangeTotalsCache.set(cacheKey, request);

  return request;
}

async function getDailyTotalsForRangeFromDayQueries(
  source: ICountingSourceDefinition,
  startDay: string,
  endExclusive: string,
): Promise<IDailyTotalPoint[]> {
  const days: string[] = [];

  for (let cursor = moment(startDay); cursor.isBefore(endExclusive, 'day'); cursor.add(1, 'day')) {
    days.push(cursor.format('YYYY-MM-DD'));
  }

  const totals = await mapWithConcurrency<
    string,
    {
      day: string;
      count: number | null;
    }
  >(days, countingDayTotalsConcurrency, async (day) => ({
    day,
    count: await getDayTotal(source, day),
  }));

  return totals
    .filter((point): point is IDailyTotalPoint => point.count !== null)
    .sort((a, b) => a.day.localeCompare(b.day));
}

function getRecordDate(
  source: ICountingSourceDefinition,
  record:
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord,
): string | null {
  if (source.type === 'paris') return (record as IParisRecord).date || null;
  if (source.type === 'nantes') return (record as INantesRecord).jour || null;
  if (source.type === 'ecoCounter') return (record as IEcoCounterRecord).date || null;
  if (source.type === 'toursMetropole') return (record as IToursMetropoleRecord).date || null;
  if (source.type === 'toulouse') return (record as IToulouseRecord).date || null;
  if (source.type === 'anglet') return (record as IAngletRecord).date || null;
  if (source.type === 'saintNazaire') return (record as ISaintNazaireRecord).date || null;

  return null;
}

function getRecordCount(
  source: ICountingSourceDefinition,
  record:
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord,
): number | null {
  if (source.type === 'paris') return (record as IParisRecord).sum_counts ?? null;
  if (source.type === 'nantes') return (record as INantesRecord).total ?? null;

  return (
    (
      record as
        | IEcoCounterRecord
        | IToursMetropoleRecord
        | IToulouseRecord
        | IAngletRecord
        | ISaintNazaireRecord
    ).counts ?? null
  );
}

function aggregateSourceSiteChannelTotalsByDay(
  source: ICountingSourceDefinition,
  site: ICountingSite,
  records: Array<
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord
  >,
): Record<string, IDailyTotalPoint[]> {
  const totalsByChannel = records.reduce<Record<string, Record<string, number>>>((res, record) => {
    const payload = getRecordSeriesPayload(source, record);
    if (!payload || payload.siteId !== site.id) return res;

    if (!res[payload.channelId]) res[payload.channelId] = {};
    res[payload.channelId][payload.day] =
      (res[payload.channelId][payload.day] || 0) + payload.count;

    return res;
  }, {});

  return Object.entries(totalsByChannel).reduce<Record<string, IDailyTotalPoint[]>>(
    (res, [channelId, totalsByDay]) => {
      res[channelId] = Object.entries(totalsByDay)
        .map(([day, count]) => ({ day, count }))
        .sort((a, b) => a.day.localeCompare(b.day));

      return res;
    },
    {},
  );
}

function getRecordSeriesPayload(
  source: ICountingSourceDefinition,
  record:
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord,
): { channelId: string; siteId: string; day: string; count: number } | null {
  const day = toDayKey(getRecordDate(source, record));
  const count = getRecordCount(source, record);
  if (!day || count === null) return null;

  if (source.type === 'paris') {
    const parisRecord = record as IParisRecord;
    if (!parisRecord.id || !parisRecord.id_compteur) return null;

    return {
      channelId: `${source.id}:${parisRecord.id_compteur}`,
      siteId: `${source.id}:${parisRecord.id}`,
      day,
      count,
    };
  }

  if (source.type === 'nantes') {
    const nantesRecord = record as INantesRecord;
    if (nantesRecord.boucle_num === undefined) return null;

    return {
      channelId: `${source.id}:${nantesRecord.boucle_num}`,
      siteId: `${source.id}:${nantesRecord.boucle_num}`,
      day,
      count,
    };
  }

  if (source.type === 'ecoCounter') {
    const ecoCounterRecord = record as IEcoCounterRecord;
    if (!ecoCounterRecord.id) return null;

    return {
      channelId: `${source.id}:${ecoCounterRecord.id}`,
      siteId: `${source.id}:${ecoCounterRecord.id}`,
      day,
      count,
    };
  }

  if (source.type === 'toursMetropole') {
    const toursRecord = record as IToursMetropoleRecord;
    if (!toursRecord.id) return null;

    return {
      channelId: `${source.id}:${toursRecord.id}`,
      siteId: `${source.id}:${toursRecord.id}`,
      day,
      count,
    };
  }

  if (source.type === 'toulouse') {
    const toulouseRecord = record as IToulouseRecord;
    if (!toulouseRecord.id) return null;
    const channelKey = getToulouseChannelKey(toulouseRecord);
    if (!channelKey) return null;

    return {
      channelId: `${source.id}:${channelKey}`,
      siteId: `${source.id}:${toulouseRecord.id}`,
      day,
      count,
    };
  }

  if (source.type === 'anglet') {
    const angletRecord = record as IAngletRecord;
    if (!angletRecord.id) return null;
    const direction =
      angletRecord.direction_name && angletRecord.direction_name !== 'UNDEFINED'
        ? angletRecord.direction_name
        : 'total';

    return {
      channelId: `${source.id}:${angletRecord.id}:${direction}`,
      siteId: `${source.id}:${angletRecord.id}`,
      day,
      count,
    };
  }

  const saintNazaireRecord = record as ISaintNazaireRecord;
  if (!saintNazaireRecord.id) return null;

  return {
    channelId: `${source.id}:${saintNazaireRecord.id}`,
    siteId: `${source.id}:${saintNazaireRecord.id}`,
    day,
    count,
  };
}

function buildChannelWeeklySeries(
  channels: ICountingChannel[],
  totalsByChannel: Record<string, IDailyTotalPoint[]>,
  weekStarts: string[],
): ICountingChannelWeeklySeries[] {
  return channels.map((channel) => {
    const weeklyTotals = aggregateDailyTotalsByWeek(totalsByChannel[channel.id] || []);

    return {
      channelId: channel.id,
      label: channel.direction ? `${channel.name} · ${channel.direction}` : channel.name,
      points: weekStarts.map((weekStart) => ({
        weekStart,
        count: weeklyTotals[weekStart] || 0,
      })),
    };
  });
}

function parseRecords(
  source: ICountingSourceDefinition,
  records: Array<
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord
  >,
  nantesCoordinates?: { [id: number]: [number, number] },
  toursMetropoleCoordinates?: { [id: string]: [number, number] },
): ICountingSite[] {
  if (source.type === 'paris') return parseParisRecords(source, records as IParisRecord[]);
  if (source.type === 'nantes')
    return parseNantesRecords(source, records as INantesRecord[], nantesCoordinates || {});
  if (source.type === 'ecoCounter')
    return parseEcoCounterRecords(source, records as IEcoCounterRecord[]);
  if (source.type === 'toursMetropole')
    return parseToursMetropoleRecords(
      source,
      records as IToursMetropoleRecord[],
      toursMetropoleCoordinates || {},
    );
  if (source.type === 'toulouse') return parseToulouseRecords(source, records as IToulouseRecord[]);
  if (source.type === 'anglet') return parseAngletRecords(source, records as IAngletRecord[]);
  if (source.type === 'saintNazaire')
    return parseSaintNazaireRecords(source, records as ISaintNazaireRecord[]);

  return [];
}

function parseParisRecords(
  source: ICountingSourceDefinition,
  records: IParisRecord[],
): ICountingSite[] {
  const latestByChannel = getLatestBy(
    aggregateParisRecordsByDay(records),
    ({ id_compteur: id }) => id,
    ({ date }) => date,
  );

  return groupChannels(
    source,
    latestByChannel
      .map((record) => {
        const lon = record.coordinates?.lon;
        const lat = record.coordinates?.lat;
        if (lon === undefined || lat === undefined || !record.id || !record.id_compteur) return;

        const channelName = record.nom_compteur || record.name || record.id_compteur;

        return {
          id: `${source.id}:${record.id_compteur}`,
          siteId: `${source.id}:${record.id}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name: channelName,
          direction: extractDirection(channelName, record.name),
          count: record.sum_counts || 0,
          countedAt: toDayKey(record.date),
          timeStep: 'day',
          coordinates: [lon, lat],
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

function parseNantesRecords(
  source: ICountingSourceDefinition,
  records: INantesRecord[],
  coordinatesMap: { [id: number]: [number, number] },
): ICountingSite[] {
  const latestByChannel = getLatestBy(
    aggregateNantesRecordsByDay(records),
    ({ boucle_num: id }) => (id !== undefined ? String(id) : undefined),
    ({ jour }) => jour,
  );

  return groupChannels(
    source,
    latestByChannel
      .map((record) => {
        if (record.boucle_num === undefined) return;

        const name = record.boucle_libelle || String(record.boucle_num);

        return {
          id: `${source.id}:${record.boucle_num}`,
          siteId: `${source.id}:${record.boucle_num}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name,
          direction: extractDirection(name),
          count: record.total || 0,
          countedAt: toDayKey(record.jour),
          timeStep: 'day',
          anomalyProbability: record.probabilite_presence_anomalie,
          coordinates: coordinatesMap[record.boucle_num] || getNantesApproximateCoordinates(name),
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

function parseEcoCounterRecords(
  source: ICountingSourceDefinition,
  records: IEcoCounterRecord[],
): ICountingSite[] {
  const latestByChannel = getLatestBy(
    aggregateEcoCounterRecordsByDay(records),
    ({ id }) => id,
    ({ date }) => date,
  );

  return groupChannels(
    source,
    latestByChannel
      .map((record) => {
        const lon = record.geo?.lon;
        const lat = record.geo?.lat;
        if (!record.id || !record.date || lon === undefined || lat === undefined) return;

        const name = record.name || record.counter || record.id;

        return {
          id: `${source.id}:${record.id}`,
          siteId: `${source.id}:${record.id}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name,
          direction: extractDirection(name),
          count: record.counts || 0,
          countedAt: toDayKey(record.date),
          timeStep: 'day',
          coordinates: [lon, lat],
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

function parseToulouseRecords(
  source: ICountingSourceDefinition,
  records: IToulouseRecord[],
): ICountingSite[] {
  const latestByChannel = getLatestBy(
    aggregateToulouseRecordsByDay(records),
    (record) => getToulouseChannelKey(record),
    ({ date }) => date,
  );

  return groupChannels(
    source,
    latestByChannel
      .map((record) => {
        if (!record.id || !record.date) return;

        const config = toulouseCounters[record.id];
        if (!config) return;

        const direction = record.direction_name || record.direction;

        return {
          id: `${source.id}:${getToulouseChannelKey(record)}`,
          siteId: `${source.id}:${record.id}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name: config.name,
          direction,
          count: record.counts || 0,
          countedAt: toDayKey(record.date),
          timeStep: 'day',
          coordinates: config.coordinates,
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

function parseToursMetropoleRecords(
  source: ICountingSourceDefinition,
  records: IToursMetropoleRecord[],
  coordinatesById: { [id: string]: [number, number] },
): ICountingSite[] {
  const latestByChannel = getLatestBy(
    aggregateToursMetropoleRecordsByDay(records),
    ({ id }) => id,
    ({ date }) => date,
  );

  return groupChannels(
    source,
    latestByChannel
      .map((record) => {
        if (!record.id || !record.date) return;

        const coordinates = coordinatesById[record.id] || getToursMetropoleCoordinates(record);
        if (!coordinates) return;

        return {
          id: `${source.id}:${record.id}`,
          siteId: `${source.id}:${record.id}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name: record.nom_compteur || record.counter || record.id,
          count: record.counts || 0,
          countedAt: toDayKey(record.date),
          timeStep: 'day',
          coordinates,
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

function parseAngletRecords(
  source: ICountingSourceDefinition,
  records: IAngletRecord[],
): ICountingSite[] {
  const latestByChannel = getLatestBy(
    aggregateAngletRecordsByDay(records),
    ({ id, direction_name: direction }) => (id ? `${id}:${direction || 'total'}` : undefined),
    ({ date }) => date,
  );

  return groupChannels(
    source,
    latestByChannel
      .map((record) => {
        const lon = record.location?.lon;
        const lat = record.location?.lat;
        if (!record.id || !record.date || lon === undefined || lat === undefined) return;

        const name = record.name || record.id;
        const direction =
          record.direction_name && record.direction_name !== 'UNDEFINED'
            ? record.direction_name
            : undefined;

        return {
          id: `${source.id}:${record.id}:${direction || 'total'}`,
          siteId: `${source.id}:${record.id}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name,
          direction,
          count: record.counts || 0,
          countedAt: toDayKey(record.date),
          timeStep: 'day',
          coordinates: [lon, lat],
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

function parseSaintNazaireRecords(
  source: ICountingSourceDefinition,
  records: ISaintNazaireRecord[],
): ICountingSite[] {
  const latestByChannel = getLatestBy(
    aggregateSaintNazaireRecordsByDay(records),
    ({ id }) => id,
    ({ date }) => date,
  );

  return groupChannels(
    source,
    latestByChannel
      .map((record) => {
        const lon = record.location?.lon;
        const lat = record.location?.lat;
        if (!record.id || !record.date || lon === undefined || lat === undefined) return;

        return {
          id: `${source.id}:${record.id}`,
          siteId: `${source.id}:${record.id}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name: record.name || record.id,
          count: record.counts || 0,
          countedAt: toDayKey(record.date),
          timeStep: 'day',
          coordinates: [lon, lat],
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

async function getGrandLyonSourceData(source: ICountingSourceDefinition): Promise<ISourceResult> {
  for (let index = 0; index < maxDays; index += 1) {
    const countedAt = moment().subtract(index, 'day').format('YYYY-MM-DD');
    const sites = await getGrandLyonSitesForDay(source, countedAt);

    if (sites.length > 0) {
      return {
        source: {
          id: source.id,
          name: source.name,
          cityName: source.cityName,
          enabled: true,
          loading: false,
          latestCountedAt: countedAt,
          sitesCount: sites.length,
          channelsCount: sites.reduce((count, site) => count + site.channels.length, 0),
        },
        sites,
      };
    }
  }

  return getEmptyEnabledSourceResult(source);
}

async function getGrandLyonSitesForDay(
  source: ICountingSourceDefinition,
  day: string,
): Promise<ICountingSite[]> {
  const [sites, channels] = await Promise.all([getGrandLyonSites(), getGrandLyonBicycleChannels()]);
  const measures = await getGrandLyonMeasures(
    day,
    channels
      .map(({ channel_id: channelId }) => channelId)
      .filter((channelId): channelId is string => Boolean(channelId)),
  );
  const sitesById = keyBy(sites, ({ site_id: id }) => id);
  const channelsById = keyBy(channels, ({ channel_id: id }) => id);
  const aggregateByChannel = aggregateGrandLyonMeasuresByChannel(measures, channelsById);

  return groupChannels(
    source,
    Object.entries(aggregateByChannel)
      .map(([channelId, count]) => {
        const channel = channelsById[channelId];
        const site = channel?.site_id ? sitesById[channel.site_id] : undefined;
        const lon = site?.lon ?? site?.xlong;
        const lat = site?.lat ?? site?.ylat;
        if (!channel || !site || lon === undefined || lat === undefined) return;

        return {
          id: `${source.id}:${channelId}`,
          siteId: `${source.id}:${site.site_id}`,
          sourceId: source.id,
          sourceName: source.name,
          cityName: source.cityName,
          name: site.site_name || site.site_id || channelId,
          direction: channel.provider_direction_name || channel.direction,
          count,
          countedAt: day,
          timeStep: 'day',
          coordinates: [lon, lat],
        };
      })
      .filter(Boolean) as ICountingChannel[],
  );
}

async function getGrandLyonSites(): Promise<IGrandLyonSiteRecord[]> {
  if (grandLyonSitesPromise) return grandLyonSitesPromise;

  grandLyonSitesPromise = getGrandLyonAll<IGrandLyonSiteRecord>(grandLyonSitesUrl, {}).catch(
    (err) => {
      grandLyonSitesPromise = null;
      throw err;
    },
  );

  return grandLyonSitesPromise;
}

async function getGrandLyonBicycleChannels(): Promise<IGrandLyonChannelRecord[]> {
  if (grandLyonBicycleChannelsPromise) return grandLyonBicycleChannelsPromise;

  grandLyonBicycleChannelsPromise = getGrandLyonAll<IGrandLyonChannelRecord>(grandLyonChannelsUrl, {
    mobility_type__in: 'BIKE,BICYCLE',
  }).catch((err) => {
    grandLyonBicycleChannelsPromise = null;
    throw err;
  });

  return grandLyonBicycleChannelsPromise;
}

async function getGrandLyonMeasures(
  day: string,
  channelIds: string[],
): Promise<IGrandLyonMeasureRecord[]> {
  return getGrandLyonMeasuresForRange(
    day,
    moment(day).add(1, 'day').format('YYYY-MM-DD'),
    channelIds,
  );
}

async function getGrandLyonMeasuresForRange(
  startDay: string,
  endExclusive: string,
  channelIds: string[],
): Promise<IGrandLyonMeasureRecord[]> {
  if (channelIds.length <= 0) return [];

  const channelIdChunks = chunkGrandLyonChannelIds(channelIds);
  const measures = await Promise.all(
    channelIdChunks.map((chunk) =>
      getGrandLyonAll<IGrandLyonMeasureRecord>(grandLyonMeasuresUrl, {
        channel_id__in: chunk.join(','),
        start_datetime__gte: startDay,
        start_datetime__lt: endExclusive,
      }),
    ),
  );

  return measures.flat();
}

async function getGrandLyonAll<T>(
  endpoint: string,
  params: { [key: string]: string },
): Promise<T[]> {
  const res: T[] = [];
  const limit = 10000;
  let nextUrl: string | null = null;

  for (let start = 1; ; start += limit) {
    const url = nextUrl ? new URL(nextUrl, endpoint) : new URL(endpoint);

    if (!nextUrl) {
      url.searchParams.set('maxfeatures', String(limit));
      url.searchParams.set('start', String(start));
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
    }

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

    const data = (await response.json()) as IGrandLyonResponse<T>;
    const values = data.values || [];
    res.push(...values);

    if (data.next) {
      nextUrl = data.next;
      continue;
    }

    nextUrl = null;

    if (values.length < limit) break;
  }

  return res;
}

function aggregateGrandLyonMeasuresByChannel(
  measures: IGrandLyonMeasureRecord[],
  channelsById: { [id: string]: IGrandLyonChannelRecord },
): { [channelId: string]: number } {
  return measures.reduce<{ [channelId: string]: number }>((res, measure) => {
    if (!measure.channel_id || !channelsById[measure.channel_id]) return res;
    res[measure.channel_id] = (res[measure.channel_id] || 0) + (measure.count || 0);

    return res;
  }, {});
}

async function getGrandLyonDailyTotalsForRange(
  startDay: string,
  endExclusive: string,
): Promise<IDailyTotalPoint[]> {
  return (await getGrandLyonRangeAggregation(startDay, endExclusive)).globalDailyTotals;
}

async function getGrandLyonSiteWeeklySeries(
  site: ICountingSite,
  startDay: string,
  endExclusive: string,
  weekStarts: string[],
): Promise<ICountingChannelWeeklySeries[]> {
  const cacheKey = `${startDay}:${endExclusive}`;
  const cachedAggregation = grandLyonRangeAggregationCache.get(cacheKey);
  if (!cachedAggregation) {
    const siteChannelIds = site.channels
      .map(({ id }) => id.replace(`${site.sourceId}:`, ''))
      .filter(Boolean);
    if (siteChannelIds.length > 0) {
      const measures = await getGrandLyonMeasuresForRange(startDay, endExclusive, siteChannelIds);
      const totalsByChannel = measures.reduce<Record<string, IDailyTotalPoint[]>>(
        (res, measure) => {
          if (!measure.channel_id || measure.count == null) return res;

          const day = toDayKey(measure.start_datetime);
          if (!day) return res;

          const channelId = `${site.sourceId}:${measure.channel_id}`;
          if (!res[channelId]) res[channelId] = [];
          res[channelId].push({ day, count: measure.count });

          return res;
        },
        {},
      );

      Object.keys(totalsByChannel).forEach((channelId) => {
        totalsByChannel[channelId] = mergeDailyTotals(totalsByChannel[channelId]);
      });

      return buildChannelWeeklySeries(site.channels, totalsByChannel, weekStarts);
    }
  }

  const internalSiteId = site.id.replace(`${site.sourceId}:`, '');
  const aggregation = await getGrandLyonRangeAggregation(startDay, endExclusive);
  const totalsByChannel = Object.entries(
    aggregation.siteChannelDailyTotals[internalSiteId] || {},
  ).reduce<Record<string, IDailyTotalPoint[]>>((res, [channelId, totals]) => {
    res[`${site.sourceId}:${channelId}`] = totals;

    return res;
  }, {});
  const labels = aggregation.siteChannelLabels[internalSiteId] || [];
  const labelsByChannelId = keyBy(labels, ({ channelId }) => channelId);
  const channels =
    site.channels.length > 0
      ? site.channels
      : labels.map(({ channelId, direction }) => ({
          id: `${site.sourceId}:${channelId}`,
          siteId: site.id,
          sourceId: site.sourceId,
          sourceName: site.sourceName,
          cityName: site.cityName,
          name: site.name,
          direction,
          count: 0,
          countedAt: site.countedAt,
          timeStep: site.timeStep,
          coordinates: site.coordinates,
        }));

  return buildChannelWeeklySeries(
    channels.map((channel) => {
      const internalChannelId = channel.id.replace(`${site.sourceId}:`, '');
      const label = labelsByChannelId[internalChannelId];

      if (!label) return channel;

      return {
        ...channel,
        direction: label.direction,
      };
    }),
    totalsByChannel,
    weekStarts,
  );
}

async function getGrandLyonRangeAggregation(
  startDay: string,
  endExclusive: string,
): Promise<IGrandLyonRangeAggregation> {
  const cacheKey = `${startDay}:${endExclusive}`;
  const cached = grandLyonRangeAggregationCache.get(cacheKey);
  if (cached) return cached;

  const request = (async () => {
    const channels = await getGrandLyonBicycleChannels();
    const channelSiteMap = channels.reduce<Record<string, { siteId: string; direction?: string }>>(
      (res, channel) => {
        if (!channel.channel_id || !channel.site_id) return res;

        res[channel.channel_id] = {
          siteId: channel.site_id,
          direction: channel.provider_direction_name || channel.direction || undefined,
        };

        return res;
      },
      {},
    );
    const bicycleChannelIds = new Set(Object.keys(channelSiteMap));
    const measures = await getGrandLyonMeasuresForRange(
      startDay,
      endExclusive,
      Array.from(bicycleChannelIds),
    );
    const globalTotalsByDay: Record<string, number> = {};
    const siteChannelTotalsByDay: Record<string, Record<string, Record<string, number>>> = {};
    const siteChannelLabelsById: Record<string, Record<string, IGrandLyonSiteChannelLabel>> = {};

    measures.forEach((measure) => {
      if (
        !measure.channel_id ||
        !bicycleChannelIds.has(measure.channel_id) ||
        measure.count == null
      ) {
        return;
      }

      const day = toDayKey(measure.start_datetime);
      const metadata = channelSiteMap[measure.channel_id];
      if (!day || !metadata) return;

      globalTotalsByDay[day] = (globalTotalsByDay[day] || 0) + measure.count;

      if (!siteChannelTotalsByDay[metadata.siteId]) siteChannelTotalsByDay[metadata.siteId] = {};
      if (!siteChannelTotalsByDay[metadata.siteId][measure.channel_id]) {
        siteChannelTotalsByDay[metadata.siteId][measure.channel_id] = {};
      }
      siteChannelTotalsByDay[metadata.siteId][measure.channel_id][day] =
        (siteChannelTotalsByDay[metadata.siteId][measure.channel_id][day] || 0) + measure.count;

      if (!siteChannelLabelsById[metadata.siteId]) siteChannelLabelsById[metadata.siteId] = {};
      siteChannelLabelsById[metadata.siteId][measure.channel_id] = {
        channelId: measure.channel_id,
        direction: metadata.direction,
      };
    });

    return {
      globalDailyTotals: dailyTotalsMapToPoints(globalTotalsByDay),
      siteChannelDailyTotals: Object.entries(siteChannelTotalsByDay).reduce<
        Record<string, Record<string, IDailyTotalPoint[]>>
      >((res, [siteId, totalsByChannel]) => {
        res[siteId] = Object.entries(totalsByChannel).reduce<Record<string, IDailyTotalPoint[]>>(
          (channelRes, [channelId, totalsByDay]) => {
            channelRes[channelId] = dailyTotalsMapToPoints(totalsByDay);

            return channelRes;
          },
          {},
        );

        return res;
      }, {}),
      siteChannelLabels: Object.entries(siteChannelLabelsById).reduce<
        Record<string, IGrandLyonSiteChannelLabel[]>
      >((res, [siteId, labelsByChannelId]) => {
        res[siteId] = Object.values(labelsByChannelId).sort((a, b) =>
          a.channelId.localeCompare(b.channelId),
        );

        return res;
      }, {}),
    };
  })().catch((err) => {
    grandLyonRangeAggregationCache.delete(cacheKey);
    throw err;
  });

  grandLyonRangeAggregationCache.set(cacheKey, request);

  return request;
}

function chunkGrandLyonChannelIds(channelIds: string[]): string[][] {
  const uniqueChannelIds = Array.from(new Set(channelIds));
  const chunks: string[][] = [];

  for (let index = 0; index < uniqueChannelIds.length; index += grandLyonMeasureChannelChunkSize) {
    chunks.push(uniqueChannelIds.slice(index, index + grandLyonMeasureChannelChunkSize));
  }

  return chunks;
}

async function getMontpellierSourceData(source: ICountingSourceDefinition): Promise<ISourceResult> {
  const counters = await getMontpellierBicycleCounters();
  const latestDay = getMontpellierLatestDay(counters);
  if (!latestDay) return getEmptyEnabledSourceResult(source);
  const activeCounters = getMontpellierCountersForDay(counters, latestDay);

  const channels = await Promise.all(
    activeCounters.map(async (counter) => getMontpellierChannel(source, counter, latestDay)),
  );
  const sites = groupChannels(
    source,
    channels.filter((channel): channel is ICountingChannel => Boolean(channel)),
  );

  return {
    source: {
      id: source.id,
      name: source.name,
      cityName: source.cityName,
      enabled: true,
      loading: false,
      latestCountedAt: latestDay,
      sitesCount: sites.length,
      channelsCount: sites.reduce((count, site) => count + site.channels.length, 0),
    },
    sites,
  };
}

async function getMontpellierChannel(
  source: ICountingSourceDefinition,
  counter: IMontpellierCounterRecord,
  day: string,
): Promise<ICountingChannel | null> {
  if (!counter.id) return null;

  const coordinates = getMontpellierCoordinates(counter);
  if (!coordinates) return null;

  return {
    id: `${source.id}:${counter.id}`,
    siteId: `${source.id}:${counter.id}`,
    sourceId: source.id,
    sourceName: source.name,
    cityName: source.cityName,
    name: getMontpellierCounterName(counter.id),
    count: await getMontpellierDayTotal(counter.id, day),
    countedAt: day,
    timeStep: 'day',
    coordinates,
  };
}

async function getMontpellierBicycleCounters(): Promise<IMontpellierCounterRecord[]> {
  if (montpellierBicycleCountersPromise) return montpellierBicycleCountersPromise;

  const url = new URL(montpellierCountersUrl);
  url.searchParams.set('limit', '1000');

  montpellierBicycleCountersPromise = fetch(url.toString())
    .then(async (response) => {
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

      const data = (await response.json()) as IMontpellierCounterRecord[];

      return data.filter(({ vehicleType }) => vehicleType?.value === 'bicycle');
    })
    .catch((err) => {
      montpellierBicycleCountersPromise = null;
      throw err;
    });

  return montpellierBicycleCountersPromise;
}

function getMontpellierLatestDay(counters: IMontpellierCounterRecord[]): string | null {
  return (
    counters
      .map(({ intensity }) => toDayKey(intensity?.metadata?.TimeInstant?.value))
      .filter(Boolean)
      .sort((a, b) => (a < b ? 1 : -1))[0] || null
  );
}

async function getMontpellierDayTotal(counterId: string, day: string): Promise<number> {
  const cacheKey = `${counterId}:${day}`;
  const cached = montpellierDayTotalsCache.get(cacheKey);
  if (cached) return cached;

  const request = (async () => {
    const url = new URL(
      `${montpellierTimeseriesBaseUrl}/${encodeURIComponent(counterId)}/attrs/intensity`,
    );
    url.searchParams.set('fromDate', `${day}T00:00:00`);
    url.searchParams.set('toDate', `${moment(day).add(1, 'day').format('YYYY-MM-DD')}T00:00:00`);

    const response = await fetch(url.toString());
    if (!response.ok) return 0;

    const data = (await response.json()) as IMontpellierTimeseriesResponse;

    return (data.values || []).reduce((count, value) => count + (value || 0), 0);
  })();

  montpellierDayTotalsCache.set(cacheKey, request);

  return request;
}

async function getMontpellierDailyTotalsForRange(
  startDay: string,
  endExclusive: string,
): Promise<IDailyTotalPoint[]> {
  const counters = await getMontpellierBicycleCounters();
  const totals = await Promise.all(
    counters.map(({ id }) =>
      id ? getMontpellierCounterDailyTotalsForRange(id, startDay, endExclusive) : [],
    ),
  );

  return mergeDailyTotals(totals.flat());
}

async function getMontpellierCounterDailyTotalsForRange(
  counterId: string,
  startDay: string,
  endExclusive: string,
): Promise<IDailyTotalPoint[]> {
  const cacheKey = `${counterId}:${startDay}:${endExclusive}`;
  const cached = montpellierRangeTotalsCache.get(cacheKey);
  if (cached) return cached;

  const request = (async () => {
    const url = new URL(
      `${montpellierTimeseriesBaseUrl}/${encodeURIComponent(counterId)}/attrs/intensity`,
    );
    url.searchParams.set('fromDate', `${startDay}T00:00:00`);
    url.searchParams.set('toDate', `${endExclusive}T00:00:00`);

    const response = await fetch(url.toString());
    if (!response.ok) {
      console.warn('[Counts][Montpellier][timeseries-range]', {
        counterId,
        endExclusive,
        startDay,
        status: response.status,
        statusText: response.statusText,
      });

      return [];
    }

    const data = (await response.json()) as IMontpellierTimeseriesResponse;
    const totalsByDay = (data.index || []).reduce<Record<string, number>>((res, date, index) => {
      const day = toDayKey(date);
      if (!day) return res;

      res[day] = (res[day] || 0) + ((data.values || [])[index] || 0);

      return res;
    }, {});

    return Object.entries(totalsByDay)
      .map(([day, count]) => ({ day, count }))
      .sort((a, b) => a.day.localeCompare(b.day));
  })();

  montpellierRangeTotalsCache.set(cacheKey, request);

  return request;
}

async function getMontpellierSiteWeeklySeries(
  site: ICountingSite,
  startDay: string,
  endExclusive: string,
  weekStarts: string[],
): Promise<ICountingChannelWeeklySeries[]> {
  const totalsEntries = await Promise.all(
    site.channels.map(async (channel) => {
      const counterId = channel.id.replace(`${site.sourceId}:`, '');

      return [
        channel.id,
        await getMontpellierCounterDailyTotalsForRange(counterId, startDay, endExclusive),
      ] as const;
    }),
  );

  return buildChannelWeeklySeries(site.channels, Object.fromEntries(totalsEntries), weekStarts);
}

function getMontpellierCountersForDay(
  counters: IMontpellierCounterRecord[],
  day: string,
): IMontpellierCounterRecord[] {
  return counters.filter(
    ({ intensity }) => toDayKey(intensity?.metadata?.TimeInstant?.value) === day,
  );
}

function getMontpellierCoordinates(counter: IMontpellierCounterRecord): [number, number] | null {
  const coordinates = counter.location?.value?.coordinates;
  if (!coordinates || coordinates.length < 2) return null;

  return [coordinates[1], coordinates[0]];
}

function getMontpellierCounterName(id: string): string {
  return id.replace('urn:ngsi-ld:EcoCounter:', 'Eco-Compteur ');
}

async function getNantesCountersCoordinates(): Promise<{ [id: number]: [number, number] }> {
  const url = new URL(nantesCountersUrl);
  url.searchParams.set('limit', '100');

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

  const data = (await response.json()) as IOpendatasoftResponse<INantesCounterRecord>;

  return (data.results || []).reduce<{ [id: number]: [number, number] }>((res, record) => {
    const lon = record.geolocalisation?.lon;
    const lat = record.geolocalisation?.lat;
    if (record.boucle_num !== undefined && lon !== undefined && lat !== undefined)
      res[record.boucle_num] = [lon, lat];

    return res;
  }, {});
}

async function getToursMetropoleCountersCoordinates(): Promise<{ [id: string]: [number, number] }> {
  const url = new URL(toursMetropoleCountersUrl);
  url.searchParams.set('limit', '100');

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

  const data = (await response.json()) as IOpendatasoftResponse<IToursMetropoleCounterRecord>;

  return (data.results || []).reduce<{ [id: string]: [number, number] }>((res, record) => {
    const lon = record.coordinates?.lon;
    const lat = record.coordinates?.lat;
    if (record.id_compteur && lon !== undefined && lat !== undefined)
      res[record.id_compteur] = [lon, lat];

    return res;
  }, {});
}

function keyBy<T>(items: T[], getKey: (item: T) => string | undefined): { [id: string]: T } {
  return items.reduce<{ [id: string]: T }>((res, item) => {
    const key = getKey(item);
    if (key) res[key] = item;

    return res;
  }, {});
}

async function mapWithConcurrency<TInput, TResult>(
  items: TInput[],
  concurrency: number,
  mapper: (item: TInput, index: number) => Promise<TResult>,
): Promise<TResult[]> {
  const results = new Array<TResult>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()));

  return results;
}

function getLatestBy<
  T extends
    | IParisRecord
    | INantesRecord
    | IEcoCounterRecord
    | IToursMetropoleRecord
    | IToulouseRecord
    | IAngletRecord
    | ISaintNazaireRecord,
>(
  records: T[],
  getId: (record: T) => string | undefined,
  getDate: (record: T) => string | undefined,
): T[] {
  const map: { [id: string]: T } = {};

  records.forEach((record) => {
    const id = getId(record);
    const countedAt = getDate(record);
    if (!id || !countedAt) return;

    const previous = map[id];
    const previousCountedAt = previous && getDate(previous);
    if (!previous || !previousCountedAt || countedAt > previousCountedAt) map[id] = record;
  });

  return Object.values(map);
}

function aggregateParisRecordsByDay(records: IParisRecord[]): IParisRecord[] {
  const map: { [key: string]: IParisRecord } = {};

  records.forEach((record) => {
    const day = toDayKey(record.date);
    if (!record.id_compteur || !day) return;

    const key = `${record.id_compteur}:${day}`;
    const previous = map[key];

    map[key] = {
      ...record,
      date: day,
      sum_counts: (previous?.sum_counts || 0) + (record.sum_counts || 0),
    };
  });

  return Object.values(map);
}

function aggregateNantesRecordsByDay(records: INantesRecord[]): INantesRecord[] {
  const map: { [key: string]: INantesRecord } = {};

  records.forEach((record) => {
    const day = toDayKey(record.jour);
    if (record.boucle_num === undefined || !day) return;

    const key = `${record.boucle_num}:${day}`;
    const previous = map[key];

    map[key] = {
      ...record,
      jour: day,
      total: (previous?.total || 0) + (record.total || 0),
      probabilite_presence_anomalie:
        previous?.probabilite_presence_anomalie ?? record.probabilite_presence_anomalie,
    };
  });

  return Object.values(map);
}

function aggregateEcoCounterRecordsByDay(records: IEcoCounterRecord[]): IEcoCounterRecord[] {
  const map: { [key: string]: IEcoCounterRecord } = {};

  records.forEach((record) => {
    const day = toDayKey(record.date);
    if (!record.id || !day || record.counts === null || record.counts === undefined) return;

    const key = `${record.id}:${day}`;
    const previous = map[key];

    map[key] = {
      ...record,
      date: day,
      counts: (previous?.counts || 0) + record.counts,
    };
  });

  return Object.values(map);
}

function aggregateToulouseRecordsByDay(records: IToulouseRecord[]): IToulouseRecord[] {
  const map: { [key: string]: IToulouseRecord } = {};

  records.forEach((record) => {
    const day = toDayKey(record.date);
    const channelKey = getToulouseChannelKey(record);
    if (!record.id || !day || !channelKey || record.counts === null || record.counts === undefined)
      return;

    const key = `${channelKey}:${day}`;
    const previous = map[key];

    map[key] = {
      ...record,
      date: day,
      counts: (previous?.counts || 0) + record.counts,
    };
  });

  return Object.values(map);
}

function aggregateToursMetropoleRecordsByDay(
  records: IToursMetropoleRecord[],
): IToursMetropoleRecord[] {
  const map: { [key: string]: IToursMetropoleRecord } = {};

  records.forEach((record) => {
    const day = toDayKey(record.date);
    if (!record.id || !day || record.counts === null || record.counts === undefined) return;

    const key = `${record.id}:${day}`;
    const previous = map[key];

    map[key] = {
      ...record,
      date: day,
      counts: (previous?.counts || 0) + record.counts,
    };
  });

  return Object.values(map);
}

function aggregateAngletRecordsByDay(records: IAngletRecord[]): IAngletRecord[] {
  const map: { [key: string]: IAngletRecord } = {};

  records.forEach((record) => {
    const day = toDayKey(record.date);
    if (!record.id || !day || record.counts === null || record.counts === undefined) return;

    const key = `${record.id}:${record.direction_name || 'total'}:${day}`;
    const previous = map[key];

    map[key] = {
      ...record,
      date: day,
      counts: (previous?.counts || 0) + record.counts,
    };
  });

  return Object.values(map);
}

function aggregateSaintNazaireRecordsByDay(records: ISaintNazaireRecord[]): ISaintNazaireRecord[] {
  const map: { [key: string]: ISaintNazaireRecord } = {};

  records.forEach((record) => {
    const day = toDayKey(record.date);
    if (!record.id || !day || record.counts === null || record.counts === undefined) return;

    const key = `${record.id}:${day}`;
    const previous = map[key];

    map[key] = {
      ...record,
      date: day,
      counts: (previous?.counts || 0) + record.counts,
    };
  });

  return Object.values(map);
}

function getToulouseChannelKey(record: IToulouseRecord): string | undefined {
  if (!record.id) return undefined;

  return `${record.id}:${record.direction_name || record.direction || 'total'}`;
}

function getToursMetropoleCoordinates(record: IToursMetropoleRecord): [number, number] | null {
  const lon = record.coordinates?.lon;
  const lat = record.coordinates?.lat;
  if (lon === undefined || lat === undefined) return null;

  return [lon, lat];
}

function groupChannels(
  source: ICountingSourceDefinition,
  channels: ICountingChannel[],
): ICountingSite[] {
  const sitesMap: { [id: string]: ICountingChannel[] } = {};

  channels.forEach((channel) => {
    if (!sitesMap[channel.siteId]) sitesMap[channel.siteId] = [];
    sitesMap[channel.siteId].push(channel);
  });

  return Object.entries(sitesMap).map(([siteId, siteChannels]) => {
    const latestCountedAt =
      siteChannels.map(({ countedAt }) => countedAt).sort((a, b) => (a < b ? 1 : -1))[0] || '';
    const latestChannels = siteChannels.filter(({ countedAt }) => countedAt === latestCountedAt);
    const firstChannel = siteChannels[0];
    const coordinates = getAverageCoordinates(siteChannels);

    return {
      id: siteId,
      sourceId: source.id,
      sourceName: source.name,
      cityName: source.cityName,
      name: getSiteName(firstChannel.name),
      count: latestChannels.reduce((count, channel) => count + channel.count, 0),
      countedAt: latestCountedAt,
      timeStep: 'day',
      freshness: getFreshness(latestCountedAt),
      coordinates,
      channels: siteChannels,
    };
  });
}

function getSiteName(name: string): string {
  return name.replace(/\s+(NO-SE|SE-NO|NE-SO|SO-NE|N-S|S-N|E-O|O-E)$/i, '');
}

function extractDirection(name: string, siteName?: string): string | undefined {
  if (siteName && name.startsWith(siteName)) {
    const direction = name.replace(siteName, '').trim();
    if (direction) return direction;
  }

  return name.match(/\b(NO-SE|SE-NO|NE-SO|SO-NE|N-S|S-N|E-O|O-E)\b/i)?.[0];
}

function getAverageCoordinates(channels: ICountingChannel[]): [number, number] {
  const coordinates = channels.reduce(
    (res, { coordinates: [lon, lat] }) => {
      res.lon += lon;
      res.lat += lat;

      return res;
    },
    { lon: 0, lat: 0 },
  );

  return [coordinates.lon / channels.length, coordinates.lat / channels.length];
}

function getNantesApproximateCoordinates(name: string): [number, number] {
  if (/willy brandt/i.test(name)) return [-1.5326, 47.2118];

  return [-1.5536, 47.2184];
}

function getFreshness(countedAt: string): TCountingFreshness {
  const age = moment().diff(moment(countedAt), 'months', true);
  if (age <= 1) return 'fresh';
  if (age <= 12) return 'late';

  return 'stale';
}

function toDayKey(date?: string | null): string {
  if (!date) return '';

  return moment.parseZone(date).format('YYYY-MM-DD');
}

function mergeDailyTotals(totals: IDailyTotalPoint[]): IDailyTotalPoint[] {
  const totalsByDay = totals.reduce<Record<string, number>>((res, { day, count }) => {
    res[day] = (res[day] || 0) + count;

    return res;
  }, {});

  return dailyTotalsMapToPoints(totalsByDay);
}

function dailyTotalsMapToPoints(totalsByDay: Record<string, number>): IDailyTotalPoint[] {
  return Object.entries(totalsByDay)
    .map(([day, count]) => ({ day, count }))
    .sort((a, b) => a.day.localeCompare(b.day));
}

function aggregateDailyTotalsByWeek(totals: IDailyTotalPoint[]): Record<string, number> {
  return totals.reduce<Record<string, number>>((res, { day, count }) => {
    const weekStart = moment(day).startOf('isoWeek').format('YYYY-MM-DD');
    res[weekStart] = (res[weekStart] || 0) + count;

    return res;
  }, {});
}

async function getKpi(
  sources: ICountingSourceDefinition[],
  results: ISourceResult[],
): Promise<ICountingKpi> {
  const resultsWithData = results.filter(({ source }) => source.enabled && source.latestCountedAt);

  if (resultsWithData.length <= 0)
    return { count: 0, countedAt: null, sourceNames: [], territories: [] };

  const sourceMap = sources.reduce<{ [id: string]: ICountingSourceDefinition }>((res, source) => {
    res[source.id] = source;

    return res;
  }, {});

  const latestByTerritory = resultsWithData.map(({ source }) => ({
    cityName: source.cityName,
    countedAt: source.latestCountedAt || null,
  }));

  console.info('[Counts][KPI] territory latest dates', {
    territories: latestByTerritory,
  });

  for (let index = 0; index < kpiMaxDays; index += 1) {
    const countedAt = moment()
      .subtract(index + 1, 'day')
      .format('YYYY-MM-DD');
    const totals = await Promise.all(
      resultsWithData.map(({ source }) => getDayTotal(sourceMap[source.id], countedAt)),
    );
    const availableTotals = totals.filter((total): total is number => total !== null);
    const missingTerritories = resultsWithData
      .filter((_, resultIndex) => totals[resultIndex] === null)
      .map(({ source: { cityName } }) => cityName);

    console.info('[Counts][KPI] day scan', {
      countedAt,
      availableCount: availableTotals.length,
      missingTerritories,
      totals,
    });

    if (availableTotals.length > 0) {
      if (missingTerritories.length > 0) {
        console.warn('[Counts][KPI] partial day, keeping total', {
          countedAt,
          missingTerritories,
        });
      }

      return {
        count: availableTotals.reduce((count, total) => count + total, 0),
        countedAt,
        sourceNames: resultsWithData
          .filter((_, resultIndex) => totals[resultIndex] !== null)
          .map(({ source: { cityName } }) => cityName),
        territories: latestByTerritory,
      };
    }
  }

  console.warn('[Counts][KPI] no usable day found in recent window', {
    territories: latestByTerritory,
  });

  return { count: 0, countedAt: null, sourceNames: [], territories: latestByTerritory };
}

function getDayWhere(source: ICountingSourceDefinition, day: string): string {
  if (source.type === 'nantes') return `${source.dateField}=date'${day}'`;
  if (source.type === 'anglet') return getAngletDayWhere(source, day);

  const nextDay = moment(day).add(1, 'day').format('YYYY-MM-DD');
  const dayFilter = `${source.dateField}>="${day}T00:00:00+00:00" and ${source.dateField}<"${nextDay}T00:00:00+00:00"`;

  if (
    source.type === 'ecoCounter' ||
    source.type === 'toursMetropole' ||
    source.type === 'toulouse' ||
    source.type === 'saintNazaire'
  )
    return `${dayFilter} and ${getNonNullCountsWhere(source)}`;

  return dayFilter;
}

function getRangeWhere(
  source: ICountingSourceDefinition,
  startDay: string,
  endExclusive: string,
): string {
  if (source.type === 'nantes') {
    return `${source.dateField}>=date'${startDay}' and ${source.dateField}<date'${endExclusive}'`;
  }
  if (source.type === 'anglet') return getAngletRangeWhere(source, startDay, endExclusive);

  const rangeFilter = `${source.dateField}>="${startDay}T00:00:00+00:00" and ${source.dateField}<"${endExclusive}T00:00:00+00:00"`;

  if (
    source.type === 'ecoCounter' ||
    source.type === 'toursMetropole' ||
    source.type === 'toulouse' ||
    source.type === 'saintNazaire'
  )
    return `${rangeFilter} and ${getNonNullCountsWhere(source)}`;

  return rangeFilter;
}

function getAngletDayWhere(source: ICountingSourceDefinition, day: string): string {
  const start = moment(day).subtract(getFranceUtcOffsetHours(day), 'hours');
  const end = moment(day).add(1, 'day').subtract(getFranceUtcOffsetHours(day), 'hours');
  const dayFilter = `${source.dateField}>="${start.format(
    'YYYY-MM-DDTHH:mm:ss',
  )}+00:00" and ${source.dateField}<"${end.format('YYYY-MM-DDTHH:mm:ss')}+00:00"`;

  return `${dayFilter} and ${getNonNullCountsWhere(source)}`;
}

function getAngletRangeWhere(
  source: ICountingSourceDefinition,
  startDay: string,
  endExclusive: string,
): string {
  const start = moment(startDay).subtract(getFranceUtcOffsetHours(startDay), 'hours');
  const end = moment(endExclusive).subtract(getFranceUtcOffsetHours(endExclusive), 'hours');
  const rangeFilter = `${source.dateField}>="${start.format(
    'YYYY-MM-DDTHH:mm:ss',
  )}+00:00" and ${source.dateField}<"${end.format('YYYY-MM-DDTHH:mm:ss')}+00:00"`;

  return `${rangeFilter} and ${getNonNullCountsWhere(source)}`;
}

function getFranceUtcOffsetHours(day: string): number {
  const timeZone = 'Europe/Paris';
  const date = new Date(`${day}T12:00:00Z`);
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const franceDate = new Date(date.toLocaleString('en-US', { timeZone }));

  return Math.round((franceDate.getTime() - utcDate.getTime()) / 3600000);
}

function getNonNullCountsWhere(source: ICountingSourceDefinition): string {
  const filters = ['counts is not null'];

  if (source.type === 'toulouse' || source.type === 'anglet') {
    filters.push('travel_mode="bike"');
  }

  if (source.type === 'saintNazaire') {
    filters.push('search(travel_modes, "vélo")');
  }

  if (source.type === 'toursMetropole') {
    filters.push('user_type="bicycle"');
  }

  return filters.join(' and ');
}
